import React, {useCallback, useEffect} from 'react';

import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext'
import {useLexicalNodeSelection} from '@lexical/react/useLexicalNodeSelection'
import {mergeRegister} from '@lexical/utils'
import {
    $getNodeByKey,
    $getSelection,
    $isNodeSelection,
    CLICK_COMMAND,
    COMMAND_PRIORITY_HIGH,
    COMMAND_PRIORITY_LOW,
    DecoratorNode,
    DOMConversionMap,
    DOMConversionOutput,
    KEY_BACKSPACE_COMMAND,
    KEY_DELETE_COMMAND,
    LexicalNode,
    NodeKey,
    SerializedLexicalNode,
} from 'lexical'

export type SerializedPageBreakNode = SerializedLexicalNode

function PageBreakComponent({nodeKey}: {nodeKey: NodeKey}) {
    const [editor] = useLexicalComposerContext()
    const [isSelected, setSelected, clearSelection] =
        useLexicalNodeSelection(nodeKey);

    const onDelete = useCallback(
        (event: KeyboardEvent) => {
            event.preventDefault()
            if (isSelected && $isNodeSelection($getSelection())) {
                const node = $getNodeByKey(nodeKey)
                if ($isPageBreakNode(node)) {
                    node.remove()
                    return true
                }
            }
            return false
        },
        [isSelected, nodeKey],
    )

    useEffect(() => {
        return mergeRegister(
            editor.registerCommand(
                CLICK_COMMAND,
                (event: MouseEvent) => {
                    const pbElem = editor.getElementByKey(nodeKey);

                    if (event.target === pbElem) {
                        if (!event.shiftKey) {
                            clearSelection()
                        }
                        setSelected(!isSelected)
                        return true
                    }

                    return false
                },
                COMMAND_PRIORITY_LOW,
            ),
            editor.registerCommand(
                KEY_DELETE_COMMAND,
                onDelete,
                COMMAND_PRIORITY_LOW,
            ),
            editor.registerCommand(
                KEY_BACKSPACE_COMMAND,
                onDelete,
                COMMAND_PRIORITY_LOW,
            ),
        )
    }, [clearSelection, editor, isSelected, nodeKey, onDelete, setSelected]);

    useEffect(() => {
        const pbElem = editor.getElementByKey(nodeKey);
        if (pbElem !== null) {
            pbElem.className = isSelected ? 'selected' : ''
        }
    }, [editor, isSelected, nodeKey])

    return null
}

export class PageBreakNode extends DecoratorNode<JSX.Element> {
    static getType(): string {
        return 'page-break'
    }

    static clone(node: PageBreakNode): PageBreakNode {
        return new PageBreakNode(node.__key)
    }

    static importJSON(serializedNode: SerializedPageBreakNode): PageBreakNode {
        return $createPageBreakNode()
    }

    static importDOM(): DOMConversionMap | null {
        return {
            figure: (domNode: HTMLElement) => {
                const tp = domNode.getAttribute('type')
                if (tp !== this.getType()) return null

                return {
                    conversion: convertPageBreakElement,
                    priority: COMMAND_PRIORITY_HIGH,
                }
            },
        }
    }

    exportJSON(): SerializedLexicalNode {
        return {
            type: this.getType(),
            version: 1,
        }
    }

    createDOM(): HTMLElement {
        const el = document.createElement('figure')
        el.style.pageBreakAfter = 'always'
        el.setAttribute('type', this.getType())
        return el
    }

    getTextContent(): string {
        return '\n'
    }

    isInline(): false {
        return false
    }

    updateDOM(): boolean {
        return false
    }

    decorate(): JSX.Element {
        return <PageBreakComponent nodeKey={this.__key} />
    }
}

function convertPageBreakElement(): DOMConversionOutput {
    return {node: $createPageBreakNode()}
}

export function $createPageBreakNode(): PageBreakNode {
    return new PageBreakNode()
}

export function $isPageBreakNode(node: LexicalNode | null | undefined): node is PageBreakNode {
    return node instanceof PageBreakNode
}
